// ForgotPasswordScreen.js
import React, { useState } from "react";
import {
  View,
  Text,
  TextInput,
  Button,
  StyleSheet,
  ScrollView,
  Image,
  TouchableOpacity,
} from "react-native";
import { MaterialIcons } from "@expo/vector-icons";
import { FASTAPI_BASE_URL } from "./config";
import { useNavigation, useRoute } from "@react-navigation/native";

const API_REQUEST_OTP = `${FASTAPI_BASE_URL}/forgot-password/request-otp`;
const API_VALIDATE_OTP = `${FASTAPI_BASE_URL}/forgot-password/validate-otp`;
const API_RESET_PASSWORD = `${FASTAPI_BASE_URL}/forgot-password/reset`;

const ForgotPasswordScreen = () => {
  const [step, setStep] = useState(1);
  const [emailOrMobile, setEmailOrMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpToken, setOtpToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const navigation = useNavigation();
  const route = useRoute();

  const prefill = route?.params?.prefillEmail || "";
  const info = route?.params?.info || "";
  const [banner, setBanner] = useState("");

  React.useEffect(() => {
    if (prefill) setEmailOrMobile(prefill);
    if (info) setBanner(info);
  }, [prefill, info]);

  const requestOTP = async () => {
    setMessage("");
    setIsError(false);

    try {
      const response = await fetch(API_REQUEST_OTP, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email_or_mobile: emailOrMobile }),
      });

      let data = null;
      let text = "";
      try {
        data = await response.json();
      } catch {
        text = await response.text();
      }

      if (response.ok) {
        setStep(2);
        setMessage("OTP sent. Check your email.");
      } else {
        setIsError(true);
        const errMsg = data?.detail?.message || data?.detail || data?.message || text || "Failed to send OTP.";
        setMessage(String(errMsg));
      }
    } catch {
      setIsError(true);
      setMessage("Network error while sending OTP.");
    }
  };

  const validateOTP = async () => {
    setMessage("");
    setIsError(false);

    const response = await fetch(API_VALIDATE_OTP, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email_or_mobile: emailOrMobile, otp }),
    });

    const data = await response.json();

    if (response.ok && data?.data?.forgot_password_token) {
      setOtpToken(data.data.forgot_password_token);
      setStep(3);
      setMessage("OTP validated. You can now reset your password.");
    } else {
      setIsError(true);
      const errMsg = data?.detail?.message || data?.detail || data?.message || "Invalid OTP. Please try again.";
      setMessage(String(errMsg));
    }
  };

  const resetPassword = async () => {
    setMessage("");
    setIsError(false);

    try {
      const response = await fetch(API_RESET_PASSWORD, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: otpToken,
        },
        body: JSON.stringify({
          email_or_mobile: emailOrMobile,
          otp_token: otpToken,
          new_password: newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setMessage("Password reset successful. Redirecting to login...");
        setTimeout(() => {
          navigation.replace("Login");
        }, 1500);
      } else {
        setIsError(true);
        const errMsg = data?.detail?.message || data?.detail || data?.message || "Failed to reset password.";
        setMessage(String(errMsg));
        setStep(1);
      }
    } catch {
      setIsError(true);
      setMessage("Network error while resetting password.");
    }
  };

  return (
    <ScrollView contentContainerStyle={styles.container}>
      <View style={styles.banner}>
        <Image source={require("../assets/ai_tutor_banner.png")} style={styles.bannerImage} />
        <Text style={styles.bannerText}>Reset your password</Text>
      </View>

      <View style={styles.loginBox}>
        {banner ? <Text style={styles.infoBanner}>{banner}</Text> : null}
        <Text style={styles.title}>Forgot Password</Text>

        {step === 1 && (
          <>
            <TextInput
              style={styles.input}
              placeholder="Email or Mobile"
              value={emailOrMobile}
              onChangeText={setEmailOrMobile}
            />
            <Button title="Send OTP" onPress={requestOTP} color="#2563eb" />
          </>
        )}

        {step === 2 && (
          <>
            <TextInput style={styles.input} placeholder="Enter OTP" value={otp} onChangeText={setOtp} />
            <Button title="Validate OTP" onPress={validateOTP} color="#2563eb" />
          </>
        )}

        {step === 3 && (
          <>
            <View style={styles.passwordWrap}>
              <TextInput
                style={styles.passwordInput}
                placeholder="New Password"
                secureTextEntry={!showPassword}
                value={newPassword}
                onChangeText={setNewPassword}
              />
              <TouchableOpacity style={styles.eyeButton} onPress={() => setShowPassword((v) => !v)}>
                <MaterialIcons
                  name={showPassword ? "visibility-off" : "visibility"}
                  size={20}
                  color="#6b7280"
                />
              </TouchableOpacity>
            </View>
            <Button title="Reset Password" onPress={resetPassword} color="#2563eb" />
          </>
        )}

        {message ? (
          <Text style={[styles.message, isError ? styles.messageError : styles.messageSuccess]}>{message}</Text>
        ) : null}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    backgroundColor: "#f3f6fb",
    alignItems: "center",
    paddingVertical: 24,
  },
  banner: {
    width: "100%",
    alignItems: "center",
    marginBottom: 20,
  },
  bannerImage: {
    width: 300,
    height: 150,
    resizeMode: "contain",
  },
  bannerText: {
    fontSize: 16,
    fontWeight: "600",
    textAlign: "center",
    color: "#334155",
    marginTop: 10,
  },
  loginBox: {
    width: "28%",
    maxWidth: 440,
    minWidth: 330,
    padding: 22,
    backgroundColor: "#fff",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#e2e8f0",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
    marginBottom: 14,
    textAlign: "center",
  },
  input: {
    width: "100%",
    padding: 12,
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderRadius: 8,
    marginBottom: 10,
    backgroundColor: "#fff",
  },
  passwordWrap: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#cbd5e1",
    borderRadius: 8,
    backgroundColor: "#fff",
    marginBottom: 10,
  },
  passwordInput: {
    flex: 1,
    padding: 12,
  },
  eyeButton: {
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  infoBanner: {
    width: "100%",
    textAlign: "center",
    backgroundColor: "#e8f2ff",
    color: "#0b57d0",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: "#cfe1ff",
  },
  message: {
    marginTop: 10,
    textAlign: "center",
  },
  messageSuccess: {
    color: "#14532d",
  },
  messageError: {
    color: "#dc2626",
  },
});

export default ForgotPasswordScreen;
